(() => {
  'use strict';

  function parsePounds(text, currency) {
    if (currency !== 'GBP' || typeof text !== 'string') return null;
    const match = /^£\s*((?:\d{1,3}(?:,\d{3})+|\d+))\.(\d{2})$/.exec(text.trim());
    if (!match) return null;
    const pennies = Number(match[1].replace(/,/g, '')) * 100 + Number(match[2]);
    return Number.isSafeInteger(pennies) && pennies > 0 ? (pennies / 100).toFixed(2) : null;
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { parsePounds };
  if (typeof document === 'undefined' || customElements.get('elite-paypal-product-message')) return;

  // A distinct namespace keeps Shopify's PayPal checkout implementation intact.
  let sdkPromise;
  function loadSdk(clientId) {
    if (window.ElitePayPalMessaging?.Messages) return Promise.resolve(window.ElitePayPalMessaging);
    if (!sdkPromise) sdkPromise = new Promise((resolve, reject) => {
      const earlyScript = document.querySelector('script[data-elite-paypal-sdk]');
      const script = earlyScript || document.createElement('script');
      const url = new URL('https://www.paypal.com/sdk/js');
      url.search = new URLSearchParams({ 'client-id': clientId, currency: 'GBP', components: 'messages' });
      if (!earlyScript) {
        script.src = url.href;
        script.async = true;
        script.dataset.namespace = 'ElitePayPalMessaging';
      }
      const timeout = setTimeout(() => reject(new Error('PayPal messaging unavailable')), 12000);
      script.onload = () => {
        clearTimeout(timeout);
        if (window.ElitePayPalMessaging?.Messages) resolve(window.ElitePayPalMessaging);
        else reject(new Error('PayPal messaging unavailable'));
      };
      script.onerror = () => { clearTimeout(timeout); reject(new Error('PayPal messaging unavailable')); };
      if (!earlyScript) document.head.append(script);
    });
    return sdkPromise;
  }

  customElements.define('elite-paypal-product-message', class extends HTMLElement {
    connectedCallback() {
      const root = this.closest(this.dataset.priceSource === 'pc' ? '[id^="asus-a31-clone-"]' : '.elite-product-main');
      this.price = root?.querySelector(this.dataset.priceSource === 'pc'
        ? '.a31-price' : '.elite-product-purchase-summary__current, .elite-product-price__current');
      this.widget = this.querySelector('[data-paypal-widget]');
      this.fallback = this.querySelector('[data-paypal-fallback]');
      if (!this.price || !this.widget || !this.fallback || !this.dataset.clientId) return;
      this.generation = 0;
      this.lastAmount = undefined;
      // The PC's payment panel is below its component controls. Keep its
      // accepted-payment text immediately, and load PayPal's offer SDK as the
      // panel approaches the screen instead of competing with the case image.
      this.active = this.hasAttribute('data-eager') || this.dataset.priceSource !== 'pc' || !('IntersectionObserver' in window);
      if (!this.active) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          if (!this.isConnected || this.active || !entries.some(entry => entry.isIntersecting)) return;
          this.active = true;
          this.visibilityObserver.disconnect();
          this.update();
        }, { rootMargin: '250px' });
        this.visibilityObserver.observe(this);
      }
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this.price, { childList: true, characterData: true, subtree: true });
      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.visibilityObserver?.disconnect();
      this.generation++;
    }

    update() {
      if (!this.active) return;
      // Read the displayed, VAT-inclusive total. A configured PC must never use
      // the case variant price. Catalog totals include the existing profit rules.
      const amount = parsePounds(this.price.textContent, this.dataset.currency);
      if (this.lastAmount === amount) return;
      this.lastAmount = amount;
      const generation = ++this.generation;
      this.removeAttribute('data-ready');
      this.widget.setAttribute('aria-hidden', 'true');
      this.widget.replaceChildren();
      this.fallback.hidden = false;
      if (!amount) return;

      loadSdk(this.dataset.clientId).then(sdk => {
        if (!this.isConnected || this.generation !== generation) return;
        const host = document.createElement('div');
        host.setAttribute('data-pp-amount', amount);
        this.widget.append(host);
        // PayPal owns the offer, eligibility wording, branding and Learn more modal.
        return sdk.Messages({
          amount: Number(amount), currency: 'GBP', pageType: 'product-details',
          style: { layout: 'text', logo: { type: 'inline' }, text: { color: 'black', size: 13 } }
        }).render(host).then(() => {
          if (!this.isConnected || this.generation !== generation || !host.querySelector('iframe')) return;
          this.widget.removeAttribute('aria-hidden');
          this.setAttribute('data-ready', '');
          this.fallback.hidden = true;
        });
      }).catch(() => {
        // Keep the accepted-payment message if PayPal is blocked or unavailable.
        // Prices, component selectors and checkout remain independent of this SDK.
      });
    }
  });
})();
